// src/prototype/data/execute/vendorLandscape.ts
// ============================================================
// VENDOR LANDSCAPE — output schema of the Vendor Landscape Agent, plus the pre-generated,
// hand-reviewed landscapes the demo ships with (E7, Oct 21).
//
// RULES THE AGENT (AND THIS FILE) MUST KEEP
//  - Models propose, rules decide: a landscape is evidence and NEVER changes a VRI score.
//  - Every vendor claim cites a source; no source, no claim. No invented vendors or figures.
//  - Neutral ordering, no paid placement, a visible "as of" date.
//  - Completeness over caution: every relevant vendor appears, competitors included.
//  - Cost and time to deploy appear only where a source supports them; otherwise null.
//
// When the live agent lands (post-GA), it returns this same shape through the adapter
// layer, cached per (client, use case), so the tab does not change.
// ============================================================

import { RESEARCHED_LANDSCAPES } from './vendorLandscapeData';

export type DeliveryPath = 'buy' | 'build' | 'partner' | 'combined';

export interface VendorSource {
  title: string;
  url: string;
  retrieved: string; // ISO date
}

export interface VendorCategory {
  name: string;
  covers: string;
  deploymentModels: string[];
  /** What decides fit against the client's EHR and installed platforms (D3, D6). */
  fitConsiderations: string;
}

export interface NamedVendor {
  name: string;
  parentCompany?: string | null;
  category: string;
  capabilitySummary: string;
  deploymentModel: string;
  integrationNotes: string;
  typicalTimeToDeploy: string | null; // only with a source
  typicalCost: string | null; // only with a source
  /** Public customer references or third-party recognition, each backed by `sources`. */
  evidence?: string[];
  sources: VendorSource[]; // at least one, always
  /** Internal metadata: on the competitor/acquirer list. Never filters the view. */
  competitorFlag?: boolean;
  confidence?: 'high' | 'medium' | 'low';
  /** Which research passes surfaced this vendor (Claude, ChatGPT, Gemini). Review metadata. */
  foundBy?: string[];
}

/**
 * Every sourced vendor is shown, competitors and potential acquirers included: a landscape
 * missing a vendor a domain expert expects is disqualifying (Chris, Oct 21). competitorFlag is
 * kept as internal metadata only and never filters or labels the client view.
 */
export function visibleVendors(l: VendorLandscape): NamedVendor[] {
  return l.vendors.filter((v) => v.sources.length > 0);
}

export interface VendorLandscape {
  useCaseId: string;
  asOf: string; // ISO date
  generation: 'pre-generated, reviewed by hand' | 'agent';
  categories: VendorCategory[];
  vendors: NamedVendor[];
  deliveryPath: { lean: DeliveryPath; note: string };
  rfpQuestions: string[];
  sources: VendorSource[];
  /** Vendors considered and not listed, with the reason (acquired, sunset, not a fit). */
  excluded?: { name: string; reason: string }[];
}

const AS_OF = '2026-10-21';

const CARE_COORDINATION: Omit<VendorLandscape, 'useCaseId'> = {
  asOf: AS_OF,
  generation: 'pre-generated, reviewed by hand',
  categories: [
    {
      name: 'Care management and transitions applications',
      covers: 'Care plans, task routing, outreach queues and post-discharge follow-up workflows for care managers.',
      deploymentModels: ['SaaS', 'Hosted'],
      fitConsiderations: 'Whether work happens inside your EHR or in a separate application with its own sign-in, and how tasks write back to the chart.',
    },
    {
      name: 'Admit, discharge and transfer (ADT) event notification',
      covers: 'Real-time encounter alerts across settings, often delivered through a health information exchange or a national network.',
      deploymentModels: ['Network service', 'HIE subscription'],
      fitConsiderations: 'Coverage of the hospitals and post-acute partners your patients actually use, and the delay between event and alert.',
    },
    {
      name: 'Post-acute network management',
      covers: 'Referral placement, preferred-provider networks and performance tracking for skilled nursing and home health partners.',
      deploymentModels: ['SaaS'],
      fitConsiderations: 'Whether your post-acute partners will use it, and how placement data joins your episode and claims data.',
    },
    {
      name: 'EHR-native care coordination modules',
      covers: 'Care management and population health functionality offered within your EHR vendor\'s own modules.',
      deploymentModels: ['EHR module'],
      fitConsiderations: 'Licensing you may already hold, and how much configuration your EHR team can absorb alongside other work.',
    },
    {
      name: 'Patient outreach services',
      covers: 'Automated and staffed outreach for follow-up scheduling, medication reconciliation reminders and check-ins.',
      deploymentModels: ['SaaS', 'Managed service'],
      fitConsiderations: 'Consent and contact-preference handling, and whether outreach results flow back into the care manager\'s queue.',
    },
  ],
  vendors: [],
  deliveryPath: {
    lean: 'combined',
    note: 'Usually a licensed workflow layer (EHR-native or a care management application) combined with in-house analytics for risk stratification and measurement. Your EHR and installed platforms decide which workflow layer fits.',
  },
  rfpQuestions: [
    'Does the workflow run inside our EHR, and what writes back to the chart?',
    'Which hospitals and post-acute partners in our market does your event notification cover today?',
    'Who owns the care management data, and in what format can we export it?',
    'How do you define and report readmission and follow-up measures, including the baseline period?',
    'What staffing do you expect from us during implementation and after go-live?',
    'What is the pricing basis (per member, per user, per encounter) and what triggers a change?',
  ],
  sources: [],
};

/** Generic landscape for use cases without a reviewed one yet. Categories only. */
function genericLandscape(useCaseId: string): VendorLandscape {
  return {
    useCaseId,
    asOf: AS_OF,
    generation: 'pre-generated, reviewed by hand',
    categories: [
      {
        name: 'EHR-native modules',
        covers: 'Functionality available within your EHR vendor\'s own modules for this kind of work.',
        deploymentModels: ['EHR module'],
        fitConsiderations: 'Licensing you may already hold and your EHR team\'s capacity.',
      },
      {
        name: 'Specialized applications',
        covers: 'Applications built for this specific workflow or problem.',
        deploymentModels: ['SaaS', 'Hosted'],
        fitConsiderations: 'Integration with your EHR and existing analytics environment, and data ownership terms.',
      },
      {
        name: 'Analytics built on your existing environment',
        covers: 'Models, reports and workflows your team builds on the data and tools you already run.',
        deploymentModels: ['In-house build'],
        fitConsiderations: 'Data access and quality for the required inputs, and analytics staffing (D1, D3).',
      },
      {
        name: 'Implementation and advisory partners',
        covers: 'Firms that configure, integrate and support adoption for this kind of initiative.',
        deploymentModels: ['Services'],
        fitConsiderations: 'Prior work with your EHR and similar health systems, and how knowledge transfers to your team.',
      },
    ],
    vendors: [],
    deliveryPath: {
      lean: 'combined',
      note: 'The right path depends on your EHR, installed platforms and analytics staffing. A reviewed landscape for this use case has not been prepared yet.',
    },
    rfpQuestions: [
      'Does it run inside our EHR workflow, and what writes back to the chart?',
      'Who owns the data it produces, and in what format can we export it?',
      'How are the outcome measures defined, and what baseline do you use?',
      'What staffing do you expect from us during and after implementation?',
      'What is the pricing basis and what triggers a change?',
    ],
    sources: [],
  };
}

/** Researched landscapes win; the authored category sketch stays as a fallback. */
const REVIEWED: Record<string, Omit<VendorLandscape, 'useCaseId'>> = {
  'opp-1': CARE_COORDINATION,
  ...RESEARCHED_LANDSCAPES,
};

/** Adapter entry point: the landscape for one use case (client-scoped once live). */
export function getVendorLandscape(useCaseId: string): VendorLandscape & { reviewed: boolean } {
  const r = REVIEWED[useCaseId];
  return r ? { ...r, useCaseId, reviewed: true } : { ...genericLandscape(useCaseId), reviewed: false };
}

export const DELIVERY_PATH_LABEL: Record<DeliveryPath, string> = {
  buy: 'Buy',
  build: 'Build',
  partner: 'Partner',
  combined: 'Combine buy and build',
};
